import { jwtAuthService } from './jwtAuthService';
import { getTenantSubdomain } from '../utils/tenantUtils';
import { endSuspendedSession, isSubscriptionInactive } from './subscriptionGate';

// Global fetch helper that attaches JWT, refreshes on 401 once, then redirects if still unauthorized
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}, retry = false): Promise<Response> {
  const requestId = Math.random().toString(36).substr(2, 9);
  
  // NUCLEAR MODE: Skip all authentication if no tokens exist
  const hasAnyToken = localStorage.getItem('jwt_access_token') || localStorage.getItem('jwt_refresh_token');
  if (!hasAnyToken) {
    console.log(`🚨 [${requestId}] NUCLEAR MODE: No tokens found, skipping authentication for request:`, {
      url: typeof input === 'string' ? input : input.toString(),
      method: init.method || 'GET'
    });
    
    // Just add tenant headers and make the request without authentication
    const opts: RequestInit = { ...init, headers: { ...(init.headers || {}) } };
    
    // Add tenant header for multi-tenant API routing
    const url = typeof input === 'string' ? input : input.toString();
    const isAuthEndpoint = url.includes('/auth/') || url.includes('/platform/auth');
    
    if (!isAuthEndpoint) {
      const tenantSubdomain = getTenantSubdomain();
      if (tenantSubdomain && tenantSubdomain !== 'enterprise') {
        (opts.headers as Record<string, string>)['X-Tenant-Subdomain'] = tenantSubdomain;
        console.log('🏢 Nuclear mode: Adding tenant header:', tenantSubdomain);
      }
    }
    
    const anonResponse = await fetch(input, opts);
    // 402 = this lab's LabBOXX subscription is suspended (entitlement gate).
    if (await isSubscriptionInactive(anonResponse)) endSuspendedSession();
    return anonResponse;
  }
  
  // Clone init to avoid mutating caller options
  const opts: RequestInit = { ...init, headers: { ...(init.headers || {}) } };

  // Attach Authorization header if missing
  const existingAuth = (opts.headers as Record<string, string>)['Authorization'] || (opts.headers as any).get?.('Authorization');
  if (!existingAuth) {
    const token = await jwtAuthService.getAccessToken();
    if (token) {
      (opts.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      console.log(`🔐 [${requestId}] Added auth token to request:`, {
        url: typeof input === 'string' ? input : input.toString(),
        method: opts.method || 'GET',
        hasToken: true
      });
    } else {
      console.log(`⚠️ [${requestId}] No token available for request:`, {
        url: typeof input === 'string' ? input : input.toString(),
        method: opts.method || 'GET'
      });
    }
  }

  // Add tenant header for multi-tenant API routing
  // BUT skip tenant headers for auth endpoints to prevent database routing conflicts
  const url = typeof input === 'string' ? input : input.toString();
  const isAuthEndpoint = url.includes('/auth/') || url.includes('/platform/auth');
  
  if (!isAuthEndpoint) {
    const tenantSubdomain = getTenantSubdomain();
    if (tenantSubdomain && tenantSubdomain !== 'enterprise') {
      (opts.headers as Record<string, string>)['X-Tenant-Subdomain'] = tenantSubdomain;
      console.log('🏢 Adding tenant header:', tenantSubdomain);
    }
  } else {
    console.log('🔐 Skipping tenant header for auth endpoint:', url);
  }

  // Set Content-Type for JSON requests if body is present and not already set
  const hasBody = opts.body && (opts.method === 'POST' || opts.method === 'PATCH' || opts.method === 'PUT');
  const existingContentType = (opts.headers as Record<string, string>)['Content-Type'] || (opts.headers as any).get?.('Content-Type');
  
  if (hasBody && !existingContentType) {
    // Check if body is JSON string or object
    const isJsonBody = typeof opts.body === 'string' || (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData));
    if (isJsonBody) {
      (opts.headers as Record<string, string>)['Content-Type'] = 'application/json';
    }
  }

  // Always include credentials for refresh cookie
  opts.credentials = opts.credentials || 'include';

  const response = await fetch(input, opts);
  
  console.log(`📡 [${requestId}] Request response:`, {
    status: response.status,
    statusText: response.statusText,
    url: typeof input === 'string' ? input : input.toString(),
    isRetry: retry
  });
  
  // 402 = this lab's LabBOXX subscription is suspended in SaaSBOXX (backend
  // entitlement gate). Every other call will fail the same way, so end the
  // session instead of rendering a screen of broken widgets.
  if (await isSubscriptionInactive(response)) {
    endSuspendedSession();
    return response;
  }

  if (response.status !== 401) return response;

  // Handle 401 responses
  if (!retry) {
    console.log(`🔄 [${requestId}] Got 401, attempting token refresh...`);
    
    // Attempt a refresh - be more resilient about this
    try {
      const refreshed = await jwtAuthService.attemptRefresh();
      if (refreshed) {
        console.log(`✅ [${requestId}] Token refreshed successfully, retrying request`);
        return authFetch(input, init, true);
      } else {
        console.log(`❌ [${requestId}] Token refresh failed - session expired`);
      }
    } catch (error) {
      console.error(`❌ [${requestId}] Token refresh error:`, error);
    }
  } else {
    console.log(`❌ [${requestId}] Second 401 after refresh - auth failure confirmed`);
  }

  // At this point either: (a) refresh already attempted & failed OR (b) second 401 after refresh
  // This means the session is truly expired or invalid
  console.log(`⏰ [${requestId}] Session expired or invalid - clearing auth and redirecting to login`);
  
  // Clear all authentication data
  localStorage.removeItem('jwt_access_token');
  localStorage.removeItem('jwt_refresh_token');
  localStorage.removeItem('jwt_user');
  localStorage.removeItem('jwt_token_expiry');
  
  // Force logout redirect with session expired message
  console.log(`🚪 [${requestId}] Redirecting to login page...`);
  
  // Use window.location to force a full page redirect
  const currentUrl = window.location.href;
  const loginUrl = `${window.location.origin}/login`;
  
  // Add a delay to allow console logs to be seen
  setTimeout(() => {
    console.log(`🔄 [${requestId}] Redirecting from ${currentUrl} to ${loginUrl}`);
    window.location.href = loginUrl;
  }, 100);
  
  // Return 401 response in case redirect doesn't happen immediately
  return response;
}

// Convenience JSON helper
export async function authFetchJson<T=any>(url: string, init: RequestInit = {}): Promise<{ ok: boolean; status: number; data?: T; error?: any; }> {
  const resp = await authFetch(url, init);
  const ct = resp.headers.get('content-type');
  let body: any = null;
  
  try {
    if (ct && ct.includes('application/json')) {
      body = await resp.json().catch(() => null);
    } else {
      // Get text response first
      const textResponse = await resp.text().catch(() => null);
      
      // Check if it looks like JSON despite wrong content-type
      if (textResponse && textResponse.trim().startsWith('{') && textResponse.trim().endsWith('}')) {
        try {
          body = JSON.parse(textResponse);
        } catch {
          body = textResponse;
        }
      } else if (textResponse && textResponse.includes('<html')) {
        // Django returned an HTML error page instead of JSON
        console.warn('⚠️ Received HTML response instead of JSON:', {
          url,
          status: resp.status,
          contentType: ct
        });
        body = {
          error: 'Server returned HTML error page instead of JSON response',
          html_response: true,
          status_code: resp.status
        };
      } else {
        body = textResponse;
      }
    }
  } catch (parseError) {
    console.error('❌ Failed to parse response:', parseError);
    body = { error: 'Failed to parse server response', parse_error: true };
  }
  
  if (!resp.ok) {
    return { ok: false, status: resp.status, error: body || resp.statusText };
  }
  return { ok: true, status: resp.status, data: body };
}
